/**
 * Batch 18-K108 — Training Companion install detection (tokenless PING/PONG).
 *
 * Separate from the K97 token handoff: this exchange carries NO token. Before
 * launching a monitored training artifact, OptiBay pings the Companion content
 * script and, if it PONGs within a short bounded timeout, treats the extension
 * as installed. If not, the caller shows the install modal instead of silently
 * launching.
 *
 * Mirrors `companionTokenHandoff.ts`'s security model:
 *   - PING is posted with a strict same-origin `targetOrigin`.
 *   - Only accepts a PONG from THIS window (`event.source === window`) on the
 *     SAME origin, of the documented type, with the SAME `requestId`.
 *   - Never logs a token (there is none here).
 *   - SSR / non-browser → resolves `{ installed: false }`.
 */

export const COMPANION_PING_MESSAGE_TYPE = 'OPTIBAY_TRAINING_COMPANION_PING';
export const COMPANION_PONG_MESSAGE_TYPE = 'OPTIBAY_TRAINING_COMPANION_PONG';
export const COMPANION_PING_DEFAULT_TIMEOUT_MS = 800;

// Real published Chrome Web Store extension id (operator-provided). The
// edge-function CORS allowlist in `_shared/authGuard.ts` carries the SAME id
// (`chrome-extension://lfhaeddajjnkicjilfaheknbjlgjnmjc`), so evidence POSTs
// from the extension are accepted — the two are reconciled.
export const COMPANION_CHROME_EXTENSION_ID = 'lfhaeddajjnkicjilfaheknbjlgjnmjc';
export const COMPANION_CHROME_WEBSTORE_URL = `https://chromewebstore.google.com/detail/${COMPANION_CHROME_EXTENSION_ID}`;
// Edge listing URL/product id not yet recorded in the repo — keep null so the
// install UI shows the Edge branch as pending (never a broken link).
export const COMPANION_EDGE_ADDONS_URL: string | null = null;

export type BrowserFamily = 'chrome' | 'edge' | 'safari' | 'other';

/** Best-effort browser family from the UA (display-only; never a gate). */
export function detectBrowserFamily(
  userAgent: string = typeof navigator !== 'undefined' ? navigator.userAgent : '',
): BrowserFamily {
  const ua = userAgent.toLowerCase();
  if (ua.includes('edg/') || ua.includes('edga/') || ua.includes('edgios/')) return 'edge';
  // Treat other Chromium browsers as chrome-compatible for the Web Store link.
  if (ua.includes('chrome') || ua.includes('chromium') || ua.includes('crios')) return 'chrome';
  // Real Safari has "safari" but NOT chrome/chromium/crios (which are handled
  // above). Exclude Firefox-iOS (fxios), which is WebKit but not Safari.
  if (ua.includes('safari') && !ua.includes('fxios')) return 'safari';
  return 'other';
}

export interface CompanionAvailabilityResult {
  installed: boolean;
  /** Extension version reported by the PONG, when installed. */
  version?: string;
}

let requestCounter = 0;
function nextRequestId(): string {
  requestCounter += 1;
  // Non-secret correlation id. Uniqueness across a session is enough; we avoid
  // Math.random()/Date.now() coupling by combining a counter with a UA-random
  // suffix when available.
  const rand =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : String(requestCounter);
  return `companion-ping-${requestCounter}-${rand}`;
}

/**
 * Ping the Companion content script and resolve whether it answered within
 * `timeoutMs`. Never rejects.
 */
export function pingCompanion(
  timeoutMs: number = COMPANION_PING_DEFAULT_TIMEOUT_MS,
): Promise<CompanionAvailabilityResult> {
  if (typeof window === 'undefined' || typeof window.postMessage !== 'function') {
    return Promise.resolve({ installed: false });
  }

  const origin = window.location.origin;
  const requestId = nextRequestId();

  return new Promise<CompanionAvailabilityResult>((resolve) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const cleanup = () => {
      window.removeEventListener('message', onMessage);
      if (timer !== undefined) clearTimeout(timer);
    };

    const finish = (result: CompanionAvailabilityResult) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(result);
    };

    function onMessage(event: MessageEvent) {
      if (event.source !== window) return;
      if (event.origin !== origin) return;
      const data = event.data as
        | { type?: unknown; requestId?: unknown; version?: unknown }
        | null;
      if (!data || typeof data !== 'object') return;
      if (data.type !== COMPANION_PONG_MESSAGE_TYPE) return;
      if (data.requestId !== requestId) return;
      finish({
        installed: true,
        version: typeof data.version === 'string' ? data.version : undefined,
      });
    }

    window.addEventListener('message', onMessage);
    timer = setTimeout(() => finish({ installed: false }), timeoutMs);

    window.postMessage(
      {
        type: COMPANION_PING_MESSAGE_TYPE,
        requestId,
        // Non-secret timestamp; the content script does not need it, but the
        // ping shape is documented to include it for future diagnostics.
        timestamp: typeof performance !== 'undefined' ? Math.floor(performance.now()) : 0,
      },
      origin,
    );
  });
}

export type CompanionReadinessState = 'unknown' | 'installed' | 'missing';

/**
 * Pure launch-gate decision. Popup-safety requires the actual `window.open`
 * to run synchronously in the click gesture, so we gate on the LAST known
 * readiness state: only a confirmed `installed` state proceeds directly; any
 * other state routes to the install modal (never a silent monitored launch).
 */
export function shouldGateMonitoredLaunch(state: CompanionReadinessState): boolean {
  return state !== 'installed';
}
